import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import styles from "./style.scss";
import { ChatForm } from "../Components/ChatForm";
import { Chat } from "./Chat";
import {
  Chat as ChatType,
  DownloadType,
  Reference as ReferenceType
} from "models/AiResearch";
import { SideReference } from "components/AiResearch/SideReference";
import CaretDownImage from "images/icon/caret-down.svg";

const SCROLL_OFFSET = 48;
const CHAT_OVERLAY_BREAKPOINT = 580;
const CHAT_OVERLAY_RELEASE_BREAKPOINT = 604;
const DEFAULT_REFERENCE_WIDTH = 436;
const REFERENCE_WIDTH_RATIO = 0.5;

interface Props {
  chat: ChatType;
  reference?: ReferenceType | null;
  handleSubmit: (text: string) => void;
  handleCopy: ({ chatIndex }: { chatIndex: number }) => void;
  handleDownload: ({
    type,
    questionId
  }: {
    type: DownloadType;
    questionId: string;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
  isReferenceLoading: boolean;
  isOpen: boolean;
  onShowReference: ({
    questionId,
    citationNo
  }: {
    questionId: string;
    citationNo: number;
  }) => void;
  clearReference: () => void;
  onOpenReferencePanel: () => void;
}

export const History: React.FC<Props> = ({
  chat,
  reference,
  handleSubmit,
  handleCopy,
  handleDownload,
  onCancel,
  isLoading,
  isReferenceLoading,
  isOpen,
  onShowReference,
  clearReference,
  onOpenReferencePanel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isReferenceWidthCustomizedRef = useRef(false);
  const [prefill, setPrefill] = useState<string>("");
  const [containerWidth, setContainerWidth] = useState(0);
  const [referenceWidth, setReferenceWidth] = useState(DEFAULT_REFERENCE_WIDTH);
  const [shouldOverlayReference, setShouldOverlayReference] = useState(false);
  const [referenceMaxWidth, setReferenceMaxWidth] = useState<number>();
  const defaultReferenceWidth =
    containerWidth > 0
      ? Math.round(containerWidth * REFERENCE_WIDTH_RATIO)
      : DEFAULT_REFERENCE_WIDTH;
  // 引用情報のデフォルトの最大幅を設定
  const defaultReferenceMaxWidth =
    containerWidth > 0 ? containerWidth : undefined;

  // 引用情報パネルがあるかのフラグ
  const hasReferencePanel = isReferenceLoading || !!reference;
  const prevHasReferencePanelRef = useRef(hasReferencePanel);

  const chatContainerWidth = !hasReferencePanel
    ? "100%"
    : shouldOverlayReference
    ? `${Math.min(containerWidth, CHAT_OVERLAY_BREAKPOINT)}px`
    : `calc(100% - ${referenceWidth}px)`;

  const handleShowReference = ({
    questionId,
    referenceId
  }: {
    questionId: string;
    referenceId: number;
  }) => {
    onShowReference({ questionId, citationNo: referenceId });
  };

  useEffect(() => {
    const element =
      (document.scrollingElement as HTMLElement | null) ||
      document.documentElement;
    if (!element) return;

    const update = () => {
      const scrollTop = element.scrollTop;
      const clientHeight = element.clientHeight;
      const scrollHeight = element.scrollHeight;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom =
        scrollTop + clientHeight >= scrollHeight - SCROLL_OFFSET;
      setShowScrollDown(isScrollable && !isAtBottom);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(element);

    return () => {
      window.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [chat]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () => {
      setContainerWidth(element.clientWidth);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(element);

    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!hasReferencePanel) {
      setShouldOverlayReference(false);
      return;
    }

    const availableWidth = containerWidth - referenceWidth;

    setShouldOverlayReference(current =>
      current
        ? availableWidth <= CHAT_OVERLAY_RELEASE_BREAKPOINT
        : availableWidth <= CHAT_OVERLAY_BREAKPOINT
    );
  }, [containerWidth, hasReferencePanel, referenceWidth]);

  useEffect(() => {
    if (hasReferencePanel && !prevHasReferencePanelRef.current) {
      isReferenceWidthCustomizedRef.current = false;
      setReferenceWidth(defaultReferenceWidth);
      // 引用情報パネルを開いたことを親コンポーネントに伝える（引用情報の切り替え時は呼び出さない）
      onOpenReferencePanel();
    }
    prevHasReferencePanelRef.current = hasReferencePanel;
  }, [defaultReferenceWidth, hasReferencePanel, onOpenReferencePanel]);

  useEffect(() => {
    if (!hasReferencePanel || isReferenceWidthCustomizedRef.current) return;

    setReferenceWidth(defaultReferenceWidth);
  }, [defaultReferenceWidth, hasReferencePanel]);

  useEffect(() => {
    if (!shouldOverlayReference) {
      setReferenceMaxWidth(undefined);
      return;
    }

    const element = chatContainerRef.current;
    if (!element) return;

    const update = () => {
      const { left } = element.getBoundingClientRect();
      setReferenceMaxWidth(
        Math.max(DEFAULT_REFERENCE_WIDTH, Math.ceil(window.innerWidth - left))
      );
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(element);
    window.addEventListener("resize", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [chatContainerWidth, shouldOverlayReference]);

  // 画面スクロール処理
  const handleScrollDown = useCallback(() => {
    const element =
      (document.scrollingElement as HTMLElement | null) ||
      document.documentElement;
    if (!element) return;

    element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
  }, []);

  const handleClearReference = () => {
    clearReference();
  };

  const handleChangeReferenceWidth = (width: number) => {
    isReferenceWidthCustomizedRef.current = width !== defaultReferenceWidth;
    setReferenceWidth(width);
  };

  return (
    <div className={cn(styles.container)} ref={containerRef}>
      <div
        className={styles.chatContainer}
        data-reference-tooltip-boundary="true"
        ref={chatContainerRef}
        style={{ width: chatContainerWidth }}
      >
        <Chat
          chat={chat}
          selectedReference={reference}
          handleSelectRecommendation={text => setPrefill(text)}
          handleShowReference={handleShowReference}
          handleCopy={handleCopy}
          handleDownload={handleDownload}
        />
        {!chat.hideChatForm && (
          <div className={styles.chatForm}>
            <div className={styles.chatFormInner}>
              {showScrollDown && (
                <button
                  className={styles.scrollButton}
                  onClick={handleScrollDown}
                >
                  <img src={CaretDownImage} alt="下へスクロール" />
                </button>
              )}
              <ChatForm
                type="history"
                prefillText={prefill}
                onSubmit={text => {
                  handleSubmit(text);
                }}
                onCancel={onCancel}
                isLoading={isLoading || isOpen}
              />
              <div className={styles.disclaimer}>
                生成AIの回答は必ずしも正しいとは限りません。正確な情報はソースを確認してください。
              </div>
            </div>
          </div>
        )}
      </div>

      {hasReferencePanel ? (
        <>
          {shouldOverlayReference ? (
            <div
              className={styles.referenceSpacer}
              style={{ width: referenceWidth }}
            />
          ) : null}
          <div
            className={cn(styles.referenceContainer, {
              [styles.referenceOverlay]: shouldOverlayReference
            })}
          >
            <SideReference
              isLoading={isReferenceLoading}
              reference={reference}
              width={referenceWidth}
              defaultWidth={defaultReferenceWidth}
              maxWidth={referenceMaxWidth ?? defaultReferenceMaxWidth}
              top={56}
              onChangeWidth={handleChangeReferenceWidth}
              onClose={handleClearReference}
              position="fixed"
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
